import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import "./FinanceV2.css";

const emptyForm = {
  account_type_id: "",
  code: "",
  name: "",
  description: "",
  parent_id: "",
  is_bank: false,
};

export default function FinanceV2ChartOfAccounts() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const [tree, setTree] = useState({});
  const [flatAccounts, setFlatAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", parent_id: "" });
  const [editSaving, setEditSaving] = useState(false);

  const canManage = [1, 3, 5].includes(parseInt(role, 10));

  const loadTree = () => {
    FinanceV2Services.getAccountsTree(branch_id).then((res) => {
      setTree(res.data?.data || {});
    });
    FinanceV2Services.getAccountsList(branch_id).then((res) => {
      setFlatAccounts(res.data?.data || []);
    });
  };

  useEffect(() => {
    loadTree();
    FinanceV2Services.getAccountTypes().then((res) => {
      setAccountTypes(res.data?.data || []);
    });
  }, [branch_id]);

  const parentOptionsForCreate = useMemo(() => {
    if (!form.account_type_id) return flatAccounts;
    return flatAccounts.filter(
      (a) => String(a.account_type_id) === String(form.account_type_id)
    );
  }, [flatAccounts, form.account_type_id]);

  const parentOptionsForEdit = useMemo(() => {
    if (!editAccount) return [];
    return flatAccounts.filter(
      (a) =>
        a.id !== editAccount.id &&
        String(a.account_type_id || a.account_type?.id) ===
          String(editAccount.account_type_id)
    );
  }, [flatAccounts, editAccount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.account_type_id || !form.code.trim() || !form.name.trim()) {
      toast.error("Account type, code, and name are required");
      return;
    }
    setSaving(true);
    try {
      await FinanceV2Services.createAccount({
        account_type_id: parseInt(form.account_type_id, 10),
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description || null,
        parent_id: form.parent_id ? parseInt(form.parent_id, 10) : null,
        is_bank: form.is_bank,
        branch_id,
      });
      toast.success(form.parent_id ? "Sub-account created" : "Account created");
      setForm(emptyForm);
      setShowForm(false);
      loadTree();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.code?.[0] ||
        "Could not create account (check code is unique)";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (account) => {
    setEditAccount(account);
    setEditForm({
      name: account.name || "",
      description: account.description || "",
      parent_id: account.parent_id || "",
    });
  };

  const saveEdit = async () => {
    if (!editAccount || !editForm.name.trim()) {
      toast.error("Account name is required");
      return;
    }
    setEditSaving(true);
    try {
      await FinanceV2Services.updateAccount(editAccount.id, {
        name: editForm.name.trim(),
        description: editForm.description || null,
        parent_id: editForm.parent_id ? parseInt(editForm.parent_id, 10) : null,
        branch_id,
      });
      toast.success("Account updated");
      setEditAccount(null);
      loadTree();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update account");
    } finally {
      setEditSaving(false);
    }
  };

  const sortAccounts = (accounts) => {
    const list = [...accounts];
    list.sort((a, b) => String(a.code).localeCompare(String(b.code)));
    const parents = list.filter((a) => !a.parent_id);
    const children = list.filter((a) => a.parent_id);
    const ordered = [];
    parents.forEach((p) => {
      ordered.push(p);
      children
        .filter((c) => c.parent_id === p.id)
        .forEach((c) => ordered.push(c));
    });
    children.forEach((c) => {
      if (!ordered.find((x) => x.id === c.id)) ordered.push(c);
    });
    return ordered;
  };

  return (
    <FinanceV2Layout>
      <div className="container-fluid px-0 py-2">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h4 className="mb-0">Chart of accounts</h4>
            <small className="text-muted">
              Create main accounts or sub-accounts (e.g. Charity Cash under Cash on Hand). Edit names anytime.
            </small>
          </div>
          <div className="d-flex gap-2">
            {canManage && (
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={() => {
                  setShowForm(!showForm);
                  setForm(emptyForm);
                }}
              >
                {showForm ? "Cancel" : "+ New account"}
              </button>
            )}
            <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
              Back
            </Link>
          </div>
        </div>

        {showForm && canManage && (
          <div className="fv2-card mb-3">
            <h6>Create new account / sub-account</h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label small">Account type</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.account_type_id}
                    onChange={(e) =>
                      setForm({ ...form, account_type_id: e.target.value, parent_id: "" })
                    }
                    required
                  >
                    <option value="">Select type</option>
                    {accountTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.normal_balance})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Parent account (optional)</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.parent_id}
                    onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                    disabled={!form.account_type_id}
                  >
                    <option value="">— Main account (no parent) —</option>
                    {parentOptionsForCreate.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    Pick a parent to create a sub-account
                  </small>
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Code</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="e.g. 1005"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Account name</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="e.g. Charity Cash"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_bank"
                      checked={form.is_bank}
                      onChange={(e) =>
                        setForm({ ...form, is_bank: e.target.checked })
                      }
                    />
                    <label className="form-check-label small" htmlFor="is_bank">
                      Bank account
                    </label>
                  </div>
                </div>
                <div className="col-md-9">
                  <label className="form-label small">Description (optional)</label>
                  <input
                    className="form-control form-control-sm"
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-sm btn-success mt-3"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save account"}
              </button>
            </form>
          </div>
        )}

        {Object.keys(tree).length === 0 ? (
          <div className="fv2-card">
            <p className="mb-0">
              No accounts yet. Run{" "}
              <Link to="/finance-v2/setup">Setup wizard</Link> to seed the default chart, or
              create accounts above.
            </p>
          </div>
        ) : (
          Object.entries(tree).map(([typeName, accounts]) => (
            <div key={typeName} className="fv2-card">
              <h6>{typeName}</h6>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Parent</th>
                    <th>Normal balance</th>
                    <th>Bank</th>
                    <th>System</th>
                    {canManage ? <th className="text-end">Action</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {sortAccounts(accounts).map((a) => (
                    <tr key={a.id}>
                      <td>{a.code}</td>
                      <td style={{ paddingLeft: a.parent_id ? 24 : undefined }}>
                        {a.parent_id ? "↳ " : ""}
                        {a.name}
                      </td>
                      <td className="small text-muted">
                        {a.parent_code
                          ? `${a.parent_code} — ${a.parent_name}`
                          : "—"}
                      </td>
                      <td className="text-capitalize">{a.normal_balance}</td>
                      <td>{a.is_bank ? "Yes" : ""}</td>
                      <td>{a.is_system ? "Yes" : ""}</td>
                      {canManage ? (
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEdit(a)}
                          >
                            Edit
                          </button>
                          {!a.is_system ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={async () => {
                                try {
                                  await FinanceV2Services.updateAccount(a.id, {
                                    is_active: false,
                                    branch_id,
                                  });
                                  toast.success("Account deactivated");
                                  loadTree();
                                } catch (err) {
                                  toast.error(err.response?.data?.message || "Failed");
                                }
                              }}
                            >
                              Deactivate
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      <Modal show={!!editAccount} onHide={() => setEditAccount(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editAccount ? (
            <>
              <p className="small text-muted mb-2">
                Code: <strong>{editAccount.code}</strong>
                {editAccount.is_system ? " (system account — name can still be edited)" : ""}
              </p>
              <div className="mb-2">
                <label className="form-label small">Account name</label>
                <input
                  className="form-control form-control-sm"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">Parent account (optional)</label>
                <select
                  className="form-select form-select-sm"
                  value={editForm.parent_id || ""}
                  onChange={(e) => setEditForm({ ...editForm, parent_id: e.target.value })}
                >
                  <option value="">— Main account (no parent) —</option>
                  {parentOptionsForEdit.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label small">Description</label>
                <input
                  className="form-control form-control-sm"
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm({ ...editForm, description: e.target.value })
                  }
                />
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setEditAccount(null)}>
            Cancel
          </Button>
          <Button variant="success" size="sm" disabled={editSaving} onClick={saveEdit}>
            {editSaving ? "Saving…" : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </FinanceV2Layout>
  );
}
